<template>
    <div id="app" class="flex-container" :class="{'is-in-app': state.isInApp}" @click="triggerRecentActivity" @keydown="triggerRecentActivity">
        <!-- Loading Spinner -->
        <div v-show="loading || !appReady" class="loader">
            <div class="spinner">
              <div class="lds-grid">
                <div></div><div></div><div></div>
                <div></div><div></div><div></div>
                <div></div><div></div><div></div>
              </div>
            </div>
        </div>
        <!-- System Info Message -->
        <div class="flex-header">
            <nav class="navbar navbar-dark system-info-message" v-if="state.system_status_messages && state.system_status_messages.length">
                <div class="w-100 px-1" v-for="msg in state.system_status_messages">
                    {{ msg.short_message }}
                    <span v-if="msg.long_message">
                        <a href @click.prevent="system_status_message_expanded = !system_status_message_expanded">
                            <span v-if="system_status_message_expanded">Show less</span>
                            <span v-else>Show more</span>
                        </a>
                    </span>
                    <div class="system-info-message__details" v-if="msg.long_message && system_status_message_expanded">
                        <strong class="pr-hf">Details: </strong>
                        {{ msg.long_message }}
                    </div>
                </div>
            </nav>

            <!-- Main Navbar -->
            <main-navbar v-if="showMainNavbar" />
            <mfa-setup-banner v-if="showMainNavbar" />

            <nav class="navbar navbar-expand-md navbar-light bg-white" v-if="showSignedOutNavbar">
                <div class="mx-auto">
                    <h1 class="h3 m-0 text-primary text-regular">
                        <router-link to="/">
                            <span class="navbar-logo">
                                <img class="navbar-logo-icon" src="@/assets/img/gmps-logo-icon-opt.svg" alt="GetMyPayStub Logo" />
                                <img class="navbar-logo-text" src="@/assets/img/gmps-logo-text-opt.svg" alt="GetMyPayStub Logo" />
                            </span>
                        </router-link>
                    </h1>
                </div>
            </nav>
        </div>
        <router-view v-if="appReady" :key="routerViewKey" v-slot="{ Component }">
            <transition name="fade" mode="out-in">
                <component
                    :is="Component"
                    class="flex-page-wrapper flex-body flex-container view"
                />
            </transition>
        </router-view>

        <session-expiring-modal ref="sessionExpiringModal" v-if="show_session_timeout_warning" />
        <logged-out-modal ref="loggedOutModal" />
        <no-access-modal ref="noAccessModal" />
        <time-mismatch-modal ref="timeMismatchModal" />
        <mfa-setup-prompt v-if="appReady && state.user" />

        <portal-target name="globalModal"></portal-target>
        <portal-target name="specialModal"></portal-target>
    </div>
</template>

<script>
import MainNavbar from '@/components/MainNavbar.vue'
import LoggedOutModal from '@/components/LoggedOutModal'
import SessionExpiringModal from '@/components/SessionExpiringModal'
import NoAccessModal from '@/components/NoAccessModal'
import TimeMismatchModal from '@/components/TimeMismatchModal'
import MfaSetupBanner from '@/components/MfaSetupBanner'
import MfaSetupPrompt from '@/components/MfaSetupPrompt'
import moment from 'moment'
import { claimVersionReload, isOneTimeTokenRoute, shouldPromptForUpdate } from '@/app-version'
import { trackBootRequests } from '@/boot-requests'

export default {
    name: 'app',
    components: {MainNavbar, LoggedOutModal, NoAccessModal, SessionExpiringModal, TimeMismatchModal, MfaSetupBanner, MfaSetupPrompt},
    data() {
        return {
            has_recent_activity: false,
            timer: null,
            time_counter: 0,
            update_prompt_shown: false,
            system_status_message_expanded: false,
        }
    },
    computed: {
        state() {return this.$store.state},
        loading() {return this.$store.state.loading},
        appReady() {
            return !this.$store.state.user || this.$store.state.security_roles_loaded
        },
        // Every navigation remounts the page. A parent route with meta.stayMounted
        // (Client.vue) remounts only when its own params change, and keys its own
        // router-view on fullPath instead.
        routerViewKey() {
            const parent = this.$route.matched[0]
            if (!parent?.meta?.stayMounted) {
                return this.$route.fullPath
            }
            return parent.path.replace(/:(\w+)/g, (_, name) => this.$route.params[name])
        },
        // The sign-up pages are for someone with no account, so they are drawn
        // signed out whatever `user` holds. That is persisted, and outlives the
        // session it came from: a native app that was signed in and then killed
        // opened its sign-up page under the last user's menu. By path rather
        // than a flag on each route, so a page added to the flow cannot miss it.
        onSignupRoute() {
            const path = (this.$route && this.$route.path) || ''
            return path === '/signup' || path.startsWith('/signup/')
        },
        showMainNavbar() {
            return Boolean(this.state.user && this.state.security_roles_loaded) && !this.onSignupRoute && !this.onMfaSetupRoute
        },
        showSignedOutNavbar() {
            return !this.state.user || this.onSignupRoute || this.onMfaSetupRoute
        },
        // the main navbar only leads to pages a setup-only session can't open
        onMfaSetupRoute() {
            return Boolean(this.$route && this.$route.path === '/account/two-step-setup')
        },
        show_session_timeout_warning() {
            if (! this.$store.state.user) {
                return false
            }
            if (! this.$store.state.session_expiry) {
                return false
            }
            // this is just to trigger the recompute, moment will get the current time
            let a = this.time_counter
            let m = moment.utc(this.$store.state.session_expiry, moment.ISO_8601, true)
            // pop up the warning 10 minutes before the session is about to end
            let res = m.isBefore(moment.utc().add(10, 'minutes'))
            return res
        },
    },
    unmounted() {
        clearInterval(this.timer)
    },
    methods: {
        triggerRecentActivity() {
            this.has_recent_activity = true
        }
    },
    mounted() {
        this.$store.dispatch('STOP_LOADING')
        this.$store.dispatch('CLOSE_LEFT_DRAWER')
        // Tracked so that AutoLogin can hold its token exchange until these
        // have answered; see boot-requests.js for what goes wrong otherwise.
        trackBootRequests([
            this.$store.dispatch('GET_SECURITY_ROLES'),
            this.$store.dispatch('GET_SYSTEM_STATUS'),
            this.$store.dispatch('GET_SYSTEM_FLAGS'),
        ])

        window.onbeforeunload = (evt) => {
            if (!this.$store.state.isCloseable) {
                evt.preventDefault()
                evt.returnValue = 'Changes you made may not be saved.'
                return 'Changes you made may not be saved.'
            }
        }

        if (!this.$store.state.session) {
            if (this.$route.meta.requiresAuth) {
                this.$store.dispatch('LOGOUT', true)
                this.$router.push({name: 'login'})
            }
        }

        this.$bus.$on('no-access', () => {
            this.$refs.noAccessModal.open()
        })

        this.$bus.$on('logged-out', () => {
            this.$refs.loggedOutModal.open()
        })

        this.$bus.$on('mfa-setup-required', () => {
            if (this.$route.name !== 'mfa-setup') {
                this.$router.push({name: 'mfa-setup'})
            }
        })

        // Cold visit with stale persisted auth (a dead server session): send the
        // user straight to login with no modal/toast, rather than the "you've been
        // signed out" UX that only makes sense for an active session that expired.
        this.$bus.$on('session-invalid', () => {
            if (this.$route.name !== 'login') {
                this.$router.push({name: 'login'})
            }
        })

        this.$bus.$on('navigation', () => {
            if (this.$refs && this.$refs.noAccessModal) {
                this.$refs.noAccessModal.close()
            }

            if (this.$refs && this.$refs.loggedOutModal) {
                this.$refs.loggedOutModal.close(true)
            }

            if (this.$refs && this.$refs.sessionExpiringModal) {
                this.$refs.sessionExpiringModal.close()
            }

            // Reloading is what actually pulls a client onto a new build. It is
            // safe to do on any navigation because the reload normally ends the
            // mismatch — but see claimVersionReload for the case where it can't,
            // and would otherwise reload this tab forever.
            //
            // Never on a one-time-token route: the reload remounts AutoLogin,
            // which re-exchanges a token that is spent, and the user lands on
            // /login instead of signed in. Spec §8.6. The next navigation picks
            // up the new build anyway.
            if (! isOneTimeTokenRoute(this.$route && this.$route.path)
                && claimVersionReload(this.$store.state.appVersionClient, this.$store.state.appVersionServer)) {
                this.$nextTick(() => {
                    // console.log('Refreshing to get new version')
                    window.location.reload()
                })
            }
        })

        // update every 30 seconds - we're really just trying to update the minutes, don't need seconds
        const heartbeatInterval = (import.meta.env.DEV) ? 30 * 1000 * 1000 : 30 * 1000;
        this.timer = setInterval(() => {
            this.time_counter += 1

            // Send a heartbeat - check for session (but make sure to set ignore_for_session_expiry=True
            // if we don't have recent activity,  so it doesn't result in indefinitely extending it).
            // Just a GET should be fine, don't need to do anything with the results since the response
            // parsers will pick up what they need from the headers.
            this.$api.get(`/heartbeat`, !this.has_recent_activity).then(resp => {
                // if the system status was updated, grab what's going on
                if (resp['system_status_message_updated'] && resp['system_status_message_updated'] != this.$store.state.system_status_message_updated) {
                    this.$store.dispatch('GET_SYSTEM_STATUS')
                }

                if (resp.time) {
                    let serverTime = resp.time.substring(0, 16);
                    let localTime = (moment.utc().toISOString().split(".")[0] + "Z").substring(0, 16);
                    let timeMismatch = (serverTime != localTime);
                    if (timeMismatch) {
                        console.error("Your computer clock or timezone is not correct, which may cause problems logging in to GetMyPayStub.")
                        // this.$refs.timeMismatchModal.open()
                    }
                }
            }).catch(() => {
                // A dropped heartbeat is a transient network blip with no UX of
                // its own, and api.js has already handled a 401 globally. Swallow
                // it so a fire-and-forget tick stays off onunhandledrejection.
            })

            // reset 'recent activity' counter
            this.has_recent_activity = false

            // If the app is more than 10 minutes out of date, let them know. Most
            // clients never get here: navigating reloads them onto the new version
            // (see the 'navigation' handler above), so this is for someone parked on
            // one page. Prompt once per page load — the heartbeat runs every 30s and
            // a toast on each tick would be unusable.
            if (!this.update_prompt_shown && shouldPromptForUpdate(this.$store.state.appVersionStaleSince)) {
                this.update_prompt_shown = true
                this.$bus.showUpdatePrompt()
            }
        }, heartbeatInterval);
    },
}
</script>
